"use client";

import { Checkbox, TextField, Typography } from "@mui/material";
import {
  SegmentForms,
  extractGeocoderResults,
  geocodeByAddress,
  hasLowerCaseLetterRegExp,
  hasMinimum6CharsRegExp,
  hasNumberRegExp,
  hasSpecialCharRegExp,
  hasUpperCaseLetterRegExp,
  passwordRequirements,
} from "@yardzen/next-client-util";

import { YardzenButton } from "@yardzen/components/yardzen-button";
import { AddressAutocompleteInput } from "@yardzen/components/address-autocomplete-input";
import { textInput } from "@yardzen/components/form-styles";
import { SubmissionError } from "@yardzen/components/login-form";
import Link from "next/link";
import * as Yup from "yup";
import { analytics } from "@yardzen/next-segment";
import { useFormik } from "formik";

interface SignUpAndSendFirstMessageProps {
  formValues: SignUpAndSendFirstMessageFormValues;
  setFormValues: (values: SignUpAndSendFirstMessageFormValues) => void;
  segmentFormName: SegmentForms;
  onSubmit: () => Promise<void>;
  messageFor?: any;
  submissionErrors?: SubmissionError[];
  setSubmissionErrors?: (errors: SubmissionError[]) => void;
  setTosChecked: (value: boolean) => void;
}

export interface SignUpAndSendFirstMessageFormValues {
  firstName: string;
  lastName: string;
  addressFull: string;
  tosChecked: boolean;
  email: string;
  password: string;
  messageToPro: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zipcode: string;
  latitude: string;
  longitude: string;
}

const validationSchema = () => {
  return Yup.object().shape({
    email: Yup.string()
      .email("Invalid email address")
      .required("Please enter your email"),
    password: Yup.string()
      .required("Please enter your password")
      .matches(hasNumberRegExp, "Password must contain at least one number")
      .matches(
        hasLowerCaseLetterRegExp,
        "Password must contain at least one lowercase letter",
      )
      .matches(
        hasUpperCaseLetterRegExp,
        "Password must contain at least one uppercase letter",
      )
      .matches(
        hasSpecialCharRegExp,
        "Password must contain at least one special character",
      )
      .matches(
        hasMinimum6CharsRegExp,
        "Password must contain at least 6 characters",
      ),
    firstName: Yup.string().required("Please enter your first name"),
    lastName: Yup.string().required("Please enter your last name"),
    addressFull: Yup.string().required("Please enter your property address"),
  });
};

export function SignUpAndSendMessageForm({
  segmentFormName,
  onSubmit,
  submissionErrors,
  setSubmissionErrors,
  formValues: value,
  setFormValues,
  messageFor,
  setTosChecked,
}: SignUpAndSendFirstMessageProps) {
  const {
    values,
    handleChange,
    handleBlur,
    touched,
    handleSubmit,
    errors: validationErrors,
    isSubmitting,
    setFieldValue,
  } = useFormik({
    initialValues: {
      ...value,
    },
    validationSchema: validationSchema,
    onSubmit: async () => {
      await onSubmit();
    },
  });

  function getError(field: keyof SignUpAndSendFirstMessageFormValues) {
    if (touched[field]) {
      return (
        submissionErrors?.find((e) => e.field === field)?.message ??
        validationErrors[field]
      );
    }
  }

  const shouldDisableSubmit =
    isSubmitting ||
    Object.keys(validationErrors).length > 0 ||
    !values.tosChecked;

  const emailSubmissionError = submissionErrors?.find(
    ({ field }) => field === "email",
  );
  const supportLink = (
    <Link href="mailto:hello@yardzen.com" style={{ color: "#c05757" }}>
      Support
    </Link>
  );

  const loginLink = (
    <Link href="/login?callbackUrl=/messages" style={{ color: "#c05757" }}>
      Log in
    </Link>
  );

  return (
    <div className="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {messageFor?.name && (
          <div className="flex flex-col space-y-2">
            <label className="font-medium">Message to {messageFor?.name}</label>
            <textarea
              className="min-h-[16rem] w-full rounded border border-gray-300 p-2 focus:border-gray-300 focus:outline-none"
              value={value.messageToPro}
              onChange={(e) => {
                handleChange(e);
                setFormValues({ ...value, messageToPro: e.target.value });
              }}
            />
          </div>
        )}

        <div className="flex flex-wrap space-y-6">
          <div className="w-full">
            <label className="font-medium">First Name</label>
            <TextField
              hiddenLabel
              name="firstName"
              sx={textInput}
              aria-label="enter your first name"
              aria-required="true"
              type="text"
              value={value.firstName}
              onBlur={(ev) => {
                analytics.trackFormFieldFilled({
                  field_name: "first_name",
                  form_name: segmentFormName,
                  text_value: ev.target.value,
                  input_type: "text",
                });
                handleBlur(ev);
              }}
              onChange={(e) => {
                handleChange(e);
                setFormValues({ ...value, firstName: e.target.value });
              }}
              variant="standard"
              placeholder="Enter your first name"
            />
            {getError("firstName") && (
              <Typography className="text-sm text-red-600">
                {getError("firstName")}
              </Typography>
            )}
          </div>

          <div className="w-full">
            <label className="font-medium">Last Name</label>
            <TextField
              hiddenLabel
              name="lastName"
              sx={textInput}
              aria-label="enter your last name"
              aria-required="true"
              type="text"
              value={value.lastName}
              onBlur={(ev) => {
                analytics.trackFormFieldFilled({
                  field_name: "last_name",
                  form_name: segmentFormName,
                  text_value: ev.target.value,
                  input_type: "text",
                });
                handleBlur(ev);
              }}
              onChange={(e) => {
                handleChange(e);
                setFormValues({ ...value, lastName: e.target.value });
              }}
              variant="standard"
              placeholder="Enter your last name"
            />
            {getError("lastName") && (
              <Typography className="text-sm text-red-600">
                {getError("lastName")}
              </Typography>
            )}
          </div>
        </div>

        <div className="w-full">
          <label className="font-medium">Property Address</label>
          <AddressAutocompleteInput
            address={value.addressFull}
            fieldName="addressFull"
            handleBlur={(e) => {
              handleBlur(e);
            }}
            handleChange={async (address) => {
              analytics.trackFormFieldFilled({
                field_name: "address",
                form_name: segmentFormName,
                input_type: "text",
              });
              setFieldValue("addressFull", address);
              if (address) {
                const geocodedAddress = await geocodeByAddress(address);
                if (geocodedAddress) {
                  extractGeocoderResults(geocodedAddress[0]).then((pieces) => {
                    setFormValues({
                      ...value,
                      addressFull: address,
                      addressLine1: `${pieces.streetNumber} ${pieces.route}`,
                      addressLine2: pieces.apt,
                      city: pieces.city,
                      state: pieces.state,
                      zipcode: pieces.zip,
                      latitude: pieces.latLng.lat.toString(),
                      longitude: pieces.latLng.lng.toString(),
                    });
                    values.addressFull = address;
                  });
                }
              }
            }}
          />
          {getError("addressFull") && (
            <Typography className="text-sm text-red-600">
              {getError("addressFull")}
            </Typography>
          )}
        </div>

        <div className="flex flex-col space-y-2">
          <label className="font-medium">Email</label>
          <TextField
            hiddenLabel
            name="email"
            sx={textInput}
            onChange={(e) => {
              handleChange(e);
              if (submissionErrors && setSubmissionErrors) {
                setSubmissionErrors([
                  ...submissionErrors.filter(({ field }) => field !== "email"),
                ]);
              }
              setFormValues({ ...value, email: e.target.value });
            }}
            onBlur={(e) => {
              handleBlur(e);
              if (segmentFormName && e.target.value) {
                analytics.trackFormFieldFilled({
                  field_name: "email",
                  form_name: segmentFormName,
                  text_value: e.target.value,
                  input_type: "text",
                });
              }
            }}
            aria-label="enter your email"
            aria-required="true"
            variant="standard"
            value={values.email}
            type="text"
            error={touched.email && Boolean(validationErrors.email)}
            placeholder="Enter your email address"
          />
          {emailSubmissionError && (
            <Typography className="text-sm text-red-600">
              {emailSubmissionError.message}{" "}
              {emailSubmissionError?.action === "try again"
                ? "Try again"
                : loginLink}{" "}
              or contact {supportLink}
            </Typography>
          )}
          {touched.email && validationErrors.email && (
            <Typography className="text-sm text-red-600">
              {validationErrors.email}. Try again or contact {supportLink}
            </Typography>
          )}
        </div>

        <div className="flex flex-col space-y-2">
          <label className="font-medium">Password</label>
          <TextField
            hiddenLabel
            name="password"
            sx={textInput}
            onChange={(e) => {
              handleChange(e);
              setFormValues({ ...value, password: e.target.value });
            }}
            onBlur={handleBlur}
            aria-label="password"
            aria-required="true"
            value={values.password}
            variant="standard"
            type="password"
            error={touched.password && Boolean(validationErrors.password)}
            placeholder="Create a password"
          />
          {touched.password && validationErrors.password && (
            <div className="mt-2 w-full">
              <Typography>Your password must have:</Typography>
              <ul className="list-inside list-disc">
                {passwordRequirements(values.password).map((req) => (
                  <li key={req.text}>
                    <span
                      className={`text-sm ${req.requirement ? "text-green-600" : "text-red-600"}`}
                    >
                      {req.text}{" "}
                      {req.element && <span>({<em>-+_!@#$%^&*.,?</em>})</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex items-start space-x-2">
          <Checkbox
            sx={{
              padding: "0 9px 0 0",
              "&.Mui-checked": { color: "prosTheme.text.secondary" },
            }}
            aria-label="terms of service"
            value={value.tosChecked}
            onChange={(e) => {
              setFormValues({ ...value, tosChecked: e.target.checked });
              values.tosChecked = e.target.checked;
              setTosChecked(e.target.checked);
              analytics.trackFormFieldFilled({
                field_name: "tos_checked",
                form_name: segmentFormName,
                text_value: String(e.target.checked),
                input_type: "checkbox",
              });
            }}
          />
          <Typography
            sx={{
              fontSize: "0.625rem",
              lineHeight: "0.9375rem",
              letterSpacing: "0.03125rem",
            }}
          >
            By creating an account, you agree to Yardzen's{" "}
            <Link
              href="https://yardzen.com/electronic-signature-disclosures"
              target="_blank"
              className="text-blue-700 underline"
            >
              Consent to Electronic Signature and Disclosures,
            </Link>{" "}
            <Link
              href="https://yardzen.com/terms-and-conditions"
              target="_blank"
              className="text-blue-700 underline"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              href="https://yardzen.com/privacy-policy"
              target="_blank"
              className="text-blue-700 underline"
            >
              Privacy Policy
            </Link>
          </Typography>
        </div>
        <div className="w-full">
          <YardzenButton
            type="submit"
            variant="contained"
            color="primary"
            disabled={shouldDisableSubmit}
            onSubmit={() => {}}
            loading={isSubmitting}
            sx={{
              width: "100%",
              fontVariant: "all-small-caps",
              fontWeight: 600,
              fontSize: "0.875rem",
            }}
          >
            {isSubmitting ? "Creating Account..." : "Get Started"}
          </YardzenButton>
        </div>
      </form>
    </div>
  );
}
